/**
 * Anthropic model capabilities
 *
 * Centralizes Anthropic model IDs and request constraints.
 *
 * Primary responsibilities:
 * - Keep Claude model IDs consistent across registries and routes
 * - Prevent unsupported sampling params on adaptive-thinking models
 *
 * Dependencies:
 * - None
 */

export const CLAUDE_FABLE_5_1_MODEL_ID = 'claude-fable-5-1' as const
export const CLAUDE_FABLE_5_MODEL_ID = 'claude-fable-5' as const
export const CLAUDE_OPUS_5_MODEL_ID = 'claude-opus-5' as const
export const CLAUDE_OPUS_4_8_MODEL_ID = 'claude-opus-4-8' as const
export const CLAUDE_OPUS_4_6_MODEL_ID = 'claude-opus-4-6' as const
export const CLAUDE_OPUS_4_5_MODEL_ID = 'claude-opus-4-5' as const
export const CLAUDE_SONNET_5_MODEL_ID = 'claude-sonnet-5' as const
export const CLAUDE_SONNET_4_5_MODEL_ID = 'claude-sonnet-4-5-20250929' as const
export const CLAUDE_HAIKU_4_5_MODEL_ID = 'claude-haiku-4-5' as const

/** Current Anthropic GA lineup (Sep 2026). */
export const CURRENT_ANTHROPIC_MODEL_IDS = [
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID,
  CLAUDE_HAIKU_4_5_MODEL_ID,
  // Capacity / escape siblings still reachable
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_5_MODEL_ID
] as const

const DEFAULT_SAMPLING_ONLY_MODELS = new Set<string>([
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID
])

const ADAPTIVE_THINKING_MODELS = new Set<string>([
  CLAUDE_FABLE_5_1_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID
])

export function supportsAnthropicCustomSampling(model: string) {
  return !DEFAULT_SAMPLING_ONLY_MODELS.has(model)
}

export function supportsAnthropicAdaptiveThinking(model: string) {
  return ADAPTIVE_THINKING_MODELS.has(model)
}

export function getAnthropicEffort(model: string, effort?: string | null) {
  if (!supportsAnthropicAdaptiveThinking(model)) return undefined
  if (effort === 'low' || effort === 'medium' || effort === 'high' || effort === 'xhigh' || effort === 'max') {
    return effort
  }
  // 'none' (Fast mode) never reaches here for an effort value — the caller
  // disables thinking entirely instead of downgrading effort — so it falls
  // through to undefined along with any other unrecognized value.
  return undefined
}

export function getAnthropicSamplingParams(model: string, temperature?: number | null) {
  if (!supportsAnthropicCustomSampling(model) || temperature === undefined || temperature === null) {
    return {}
  }

  return { temperature }
}
